import { PerfectScaleAgentUpgradeStatus } from "@doitintl/cmp-models";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Stack, Tooltip, Typography } from "@mui/material";

import { useFsk8sConfig } from "../../hooks";
import StatusChipWithTooltip, { type StatusChipWithTooltipProps } from "./Components/StatusChipWithTooltip";
import { ClusterAgentStatus, type ClusterTableItem } from "./types";

interface AgentStatusProps {
  cluster: ClusterTableItem;
}

const AgentStatus = ({ cluster }: AgentStatusProps) => {
  const [config] = useFsk8sConfig();

  const getLastUpdateTooltipContent = () => {
    const lastUpdateTime = cluster.perfectScale?.AgentLatestStatusUpdateTime;

    if (!lastUpdateTime) {
      return undefined;
    }

    const formattedDate = lastUpdateTime.toDate().toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });

    return (
      <Stack>
        <span>Status last updated:</span>
        <span>{formattedDate}</span>
      </Stack>
    );
  };

  const statusLastUpdated = getLastUpdateTooltipContent();

  const statusConfigs: Record<ClusterAgentStatus, StatusChipWithTooltipProps> = {
    [ClusterAgentStatus.TRANSMITTING]: {
      color: "success",
      label: "Transmitting data",
      tooltipContent: statusLastUpdated,
      icon: <InfoOutlinedIcon />,
    },
    [ClusterAgentStatus.WAITING_FOR_DATA]: {
      color: "warning",
      label: "Waiting for data",
      tooltipContent: statusLastUpdated,
      icon: <InfoOutlinedIcon />,
    },
    [ClusterAgentStatus.STOPPED_TRANSMITTING]: {
      color: "default",
      label: "Stopped transmitting data",
      tooltipContent: statusLastUpdated,
      icon: <InfoOutlinedIcon />,
    },
    [ClusterAgentStatus.NEVER_TRANSMITTED]: {
      color: "default",
      label: "Never transmitted data",
      tooltipContent: "This cluster's agent has never transmitted data",
      icon: <InfoOutlinedIcon />,
    },
  };

  const upgradeMessage: Partial<Record<PerfectScaleAgentUpgradeStatus, string>> = {
    [PerfectScaleAgentUpgradeStatus.UPGRADE_REQUIRED]: "Upgrade required",
    [PerfectScaleAgentUpgradeStatus.UPGRADE_AVAILABLE]: "Upgrade available",
  };

  const statusConfig = statusConfigs[cluster.agentStatus];
  const upgradeStatus = cluster.perfectScale?.AgentUpgradeStatus;
  const upgradeMessageContent = upgradeStatus ? upgradeMessage[upgradeStatus] : undefined;

  if (!statusConfig) {
    return null;
  }

  return (
    <Stack gap="4px">
      <StatusChipWithTooltip {...statusConfig} />
      {!!upgradeMessageContent && (
        <Tooltip title={`Latest version available: ${config?.agentLatestTag}`} sx={{ cursor: "help" }}>
          <Typography variant="body2">{upgradeMessageContent}</Typography>
        </Tooltip>
      )}
    </Stack>
  );
};

export default AgentStatus;
